import { Injectable } from '@angular/core';
import { ChannelTypeE } from '@enums/channel.enum';
import { MessageTypeE, SenderTypeE } from '@enums/message.enum';
import { FileI } from '@interfaces/file.interface';
import { MessageI } from '@interfaces/message.interface';
import { DownloadFileParamsT, MessageTypeT } from '@omnitypes/message.type';
import { firstValueFrom } from 'rxjs';
import { UserHelperService } from './user-helper.service';
import { ChannelE } from '@interfaces/channel.interface';
import { FileMessageUploadedI } from '@interfaces/file-message-upload.interface';
import { FileUploadService } from '@services/files/file-upload.service';
import { MessagesService } from '@services/chat-persistence/messages.service';
import { Buffer } from 'buffer';
import { newUUID } from 'jssip/lib/Utils';
import * as mime from 'mime';
import { MessageGroupI } from '@interfaces/group.interface';
import { sanitizeAttachmentFilename } from '@helpers/sanitize-attachment-filename.helper';

@Injectable({ providedIn: 'root' })
export class MessagesHelperService {
  constructor(
    private readonly userHelper: UserHelperService,
    private fileUploadService: FileUploadService,
    private messagesService: MessagesService
  ) {}

  private depreciatedBucket = 'omni-chat-upload';

  public getSenderTypeByMessage(message: MessageI | MessageGroupI): 'agent' | 'contact' {
    const senderId = message?.sender?.senderId ?? message?.sender?._id;

    if (senderId && this.userHelper.verifyIfUserIsCurrentUser(senderId)) {
      return SenderTypeE.AGENT;
    } else {
      if (message.channelType === ChannelE.INTERNAL) {
        return SenderTypeE.CONTACT;
      }

      return [
        SenderTypeE.AGENT,
        SenderTypeE.FLOW,
        SenderTypeE.BOT,
        SenderTypeE.WORKFLOW,
        SenderTypeE.AI_AGENT,
      ].includes(message?.sender?.type)
        ? SenderTypeE.AGENT
        : SenderTypeE.CONTACT;
    }
  }

  private getFileName(file: FileI): string {
    const { name, fileName, Key } = file;
    return name || fileName || Key || 'arquivo';
  }

  public getMessageType(messageType: string) {
    //TODO: substituir esse switch por um objeto
    switch (messageType) {
      case MessageTypeE.IMAGE:
        return 'image';
      case MessageTypeE.FILE:
        return 'document';
      case MessageTypeE.AUDIO:
        return 'audio';
      case MessageTypeE.STICKER:
        return 'image';
      case MessageTypeE.VIDEO:
        return 'video';
      default:
        return 'document';
    }
  }

  public async downloadFile(fileParams: DownloadFileParamsT): Promise<void> {
    const { file, messageType, channelType, senderType, instanceKey, mimeType } = fileParams;
    try {
      const fileUrl = await this.getFileUrl({
        file,
        channelType,
        messageType,
        senderType,
        instanceKey,
        mimeType,
      });
      const fileName = this.getFileName(file);
      return this.createDownloadLink(fileUrl, fileName);
    } catch (error) {
      throw new Error(`Error downloading file ::> ${error}`);
    }
  }

  public async getFileUrl(fileParams: DownloadFileParamsT, download = true): Promise<string> {
    const { file, channelType, messageType, senderType, instanceKey, chatData } = fileParams;
    const { url, Location, Bucket } = file;
    try {
      if (!Bucket && this.isMediaDownloadable(channelType, senderType) && download) {
        const media = await this.downloadMediaWhatsapp(file, messageType, instanceKey);
        if (chatData && media) {
          this.updateMessageFile(media, chatData, fileParams);
        }

        return media;
      } else {
        const media: string = Location ?? url ?? '';
        if (chatData && (Location || url) && Bucket === this.depreciatedBucket) {
          this.updateMessageFile(media, chatData);
        }

        return media;
      }
    } catch (error) {
      throw new Error(`Error getting file url ::> ${error}`);
    }
  }

  private async updateMessageFile(data: string, chatData: FileMessageUploadedI, fileParams?: DownloadFileParamsT) {
    try {
      const { messageType, mimeType } = fileParams || {};
      const { fileName, messageId } = chatData;
      const { data: file, isBase64 } = await this.dataToBlobOrBase64(data);

      if (!fileName && !mimeType) {
        return;
      } else if (!fileName && mimeType) {
        const extension = mime.getExtension(mimeType);
        chatData.fileName = `${newUUID()}.${extension === 'oga' ? 'ogg' : extension}`;
      }

      const type = messageType ?? this.getContentType(fileName);
      const updatedChatData = {
        ...chatData,
        ...(isBase64 ? { contentEncoding: 'base64' } : {}),
      };

      if (file) {
        const fileUploadRequest =
          type === 'audio'
            ? this.fileUploadService.sendAudioMessageToFileService(file, updatedChatData)
            : this.fileUploadService.sendFileMessageToFileService(file, updatedChatData);
        const updatedFile = await firstValueFrom(fileUploadRequest);
        const convertedType = type === 'document' ? 'file' : type;

        const content = {
          [convertedType]: updatedFile,
        };

        firstValueFrom(this.messagesService.updateMessageContent(messageId!, content));
      }
    } catch (error) {
      return;
    }
  }

  public getContentType(fileName: string) {
    const extension = fileName.split('.').reverse()[0];

    switch (extension) {
      case 'png':
      case 'jpeg':
      case 'jpg':
        return 'image';
      case 'pdf':
      case 'doc':
      case 'docx':
        return 'document';
      case 'mp3':
      case 'wav':
      case 'aac':
      case 'amr':
      case 'mpeg':
      case 'ogg':
      case 'm4a':
      case 'oga':
        return 'audio';
      case '3gpp':
      case 'mp4':
      case 'webm':
        return 'video';
    }

    return 'document';
  }

  private async dataToBlobOrBase64(data: string) {
    try {
      const isURL = data.startsWith('http');

      if (isURL) {
        const response = await fetch(data);
        if (response.ok) {
          return {
            data: await response.blob(),
            isBase64: false,
          };
        } else {
          throw new Error(`Failed to fetch file from URL: ${data}`);
        }
      } else {
        const base64Data = data.includes(',') ? data.split(',')[1] : data;

        const fileBuffer = Buffer.from(base64Data, 'base64');

        return {
          data: new Blob([fileBuffer]),
          isBase64: true,
        };
      }
    } catch (error) {
      throw new Error(`Error updating message file: ${error}`);
    }
  }

  public isMediaDownloadable(channelType: string, senderType: string): boolean {
    return channelType === ChannelTypeE.WHATSAPP && senderType === SenderTypeE.CONTACT;
  }

  private async downloadMediaWhatsapp(file: FileI, messageType: string, instanceKey?: string): Promise<string> {
    console.log('deprecated megaapi');
    return '';
  }

  public async createDownloadLink(url: string, fileName: string, type?: MessageTypeE | MessageTypeT): Promise<void> {
    if (!type || ![MessageTypeE.IG_STORY, MessageTypeE.IG_REEL, MessageTypeE.IG_COMMENT].includes(type as any)) {
      url = `${url}?v=${Date.now()}`;
    }

    const link = document.createElement('a');
    const blob = await fetch(url).then((res) => res.blob());
    const href = URL.createObjectURL(blob);
    link.href = href;
    link.target = '_blank';
    link.download = sanitizeAttachmentFilename(fileName);
    document.body.appendChild(link);

    link.click();
    link.remove();
  }
}
